import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';

export const publicApiQueryCommand: SelfContainedCommand = {
  id: 'public_api_query',
  description: `🌍 PUBLIC API QUERY: Make HTTP requests to public APIs that require no authentication.

**WHEN TO USE THIS TOOL:**
Use this tool when you need to fetch data from a public, no-auth-required API. This tool makes the request directly from the frontend.

For APIs that require API keys or OAuth, use \`api_connect\` instead.

**PARAMETERS:**
- \`url\` (required): Full URL to call, e.g. "https://pokeapi.co/api/v2/pokemon/pikachu"
- \`method\` (optional): HTTP method — GET (default), POST, PUT, PATCH, DELETE
- \`queryParams\` (optional): Object of query string parameters (appended to the URL)
- \`headers\` (optional): Additional request headers
- \`body\` (optional): Request body for POST/PUT/PATCH

---

## CATALOG OF KNOWN PUBLIC APIs (no API key required)

### Animals
- Random dog image: GET https://dog.ceo/api/breeds/image/random
- Random dog image (alt): GET https://random.dog/woof.json
- Random fox image: GET https://randomfox.ca/floof/
- Cat as a service (JSON): GET https://cataas.com/cat?json=true
- Random cat fact: GET https://meowfacts.herokuapp.com/
- Random zoo animal: GET https://zoo-animal-api.herokuapp.com/animals/random
- Shiba Inu images: GET https://shibe.online/api/shibes?count=1
- HTTP status cat image: GET https://http.cat/{status_code} (e.g. /404)
- HTTP status dog image: GET https://http.dog/{status_code}.json

### Anime
- Random anime (MyAnimeList via Jikan): GET https://api.jikan.moe/v4/random/anime
- Search anime: GET https://api.jikan.moe/v4/anime?q={query}&limit=10
- Anime quote: GET https://animechan.io/api/v1/quotes/random
- Neko image: GET https://nekos.best/api/v2/neko

### Art & Museums
- Art Institute of Chicago artworks: GET https://api.artic.edu/api/v1/artworks?limit=10
- Art Institute single artwork: GET https://api.artic.edu/api/v1/artworks/{id}
- Met Museum objects: GET https://collectionapi.metmuseum.org/public/collection/v1/objects/{objectID}
- Met Museum search: GET https://collectionapi.metmuseum.org/public/collection/v1/search?q={query}

### Books & Literature
- Random poem: GET https://poetrydb.org/random
- Poems by author: GET https://poetrydb.org/author/{author}
- Bible verse: GET https://bible-api.com/{reference} (e.g. /John+3:16)
- Open Library search: GET https://openlibrary.org/search.json?q={query}&limit=10
- Open Library book: GET https://openlibrary.org/works/{id}.json
- Gutendex (Project Gutenberg): GET https://gutendex.com/books?search={query}

### Entertainment / Jokes / Trivia
- Random Chuck Norris joke: GET https://api.chucknorris.io/jokes/random
- Chuck Norris joke by category: GET https://api.chucknorris.io/jokes/random?category={category}
- Chuck Norris categories: GET https://api.chucknorris.io/jokes/categories
- Dad joke (JSON): GET https://icanhazdadjoke.com/ (add header Accept: application/json)
- Random useless fact: GET https://uselessfacts.jsph.pl/random.json?language=en
- Fun fact: GET https://api.aakhilv.me/fun/facts
- Tech phrase: GET https://techy-api.vercel.app/api/text
- Corporate BS phrase: GET https://corporatebs-generator.sameerkumar.website/
- Random activity idea: GET https://www.boredapi.com/api/activity
- Open Trivia questions: GET https://opentdb.com/api.php?amount=10&type=multiple
- Open Trivia categories: GET https://opentdb.com/api_category.php

### Games & Comics
- Pokémon by name or ID: GET https://pokeapi.co/api/v2/pokemon/{name_or_id}
- Pokémon list: GET https://pokeapi.co/api/v2/pokemon?limit=20
- Pokémon type: GET https://pokeapi.co/api/v2/type/{name}
- Rick and Morty characters: GET https://rickandmortyapi.com/api/character
- Rick and Morty single character: GET https://rickandmortyapi.com/api/character/{id}
- Rick and Morty episodes: GET https://rickandmortyapi.com/api/episode
- Rick and Morty locations: GET https://rickandmortyapi.com/api/location
- Zelda BOTW compendium entry: GET https://botw-compendium.herokuapp.com/api/v2/entry/{name}
- Zelda BOTW all entries: GET https://botw-compendium.herokuapp.com/api/v2/compendium/all
- Disney character: GET https://api.disneyapi.dev/character/{id}
- Disney characters list: GET https://api.disneyapi.dev/character?pageSize=10
- Age of Empires II unit: GET https://age-of-empires-2-api.herokuapp.com/api/v1/unit/{id}
- DnD 5e spell: GET https://www.dnd5eapi.co/api/spells/{index}
- DnD 5e monsters: GET https://www.dnd5eapi.co/api/monsters
- xkcd latest comic: GET https://xkcd.com/info.0.json
- xkcd specific comic: GET https://xkcd.com/{number}/info.0.json

### Weather
- Open-Meteo current weather: GET https://api.open-meteo.com/v1/forecast?latitude={lat}&longitude={lon}&current_weather=true
- Open-Meteo hourly forecast: GET https://api.open-meteo.com/v1/forecast?latitude={lat}&longitude={lon}&hourly=temperature_2m,windspeed_10m
- US National Weather Service point: GET https://api.weather.gov/points/{lat},{lon}
- US National Weather Service alerts: GET https://api.weather.gov/alerts/active?area={state}
- 7Timer weather: GET https://www.7timer.info/bin/api.pl?lon={lon}&lat={lat}&product=astro&output=json

### Science & Space
- NASA Astronomy Picture of the Day: GET https://api.nasa.gov/planetary/apod?api_key=DEMO_KEY
- NASA Mars Rover photos: GET https://api.nasa.gov/mars-photos/api/v1/rovers/curiosity/photos?sol=1000&api_key=DEMO_KEY
- SpaceX latest launch: GET https://api.spacexdata.com/v4/launches/latest
- SpaceX upcoming launches: GET https://api.spacexdata.com/v4/launches/upcoming
- SpaceX all rockets: GET https://api.spacexdata.com/v4/rockets
- ISS current location: GET https://api.wheretheiss.at/v1/satellites/25544
- ISS astronauts: GET https://corquaid.github.io/international-space-station-APIs/JSON/people-in-space.json
- USGS earthquakes (past day, M2.5+): GET https://earthquake.usgs.gov/fdsnws/event/1/query?format=geojson&minmagnitude=2.5&limit=20
- Sunrise/sunset times: GET https://api.sunrise-sunset.org/json?lat={lat}&lng={lon}&formatted=0
- Launch Library upcoming launches: GET https://ll.thespacedevs.com/2.2.0/launch/upcoming/?limit=10

### Geography & Countries
- All countries (basic): GET https://restcountries.com/v3.1/all?fields=name,capital,population,flags
- Country by name: GET https://restcountries.com/v3.1/name/{name}
- Country by code: GET https://restcountries.com/v3.1/alpha/{code}
- Countries by region: GET https://restcountries.com/v3.1/region/{region}
- US ZIP code lookup: GET https://api.zippopotam.us/us/{zipcode}
- IP geolocation (no auth): GET https://ipapi.co/{ip}/json/ (or /json/ with no IP for your own)
- Your IP address: GET https://api.ipify.org?format=json
- UK postcodes: GET https://api.postcodes.io/postcodes/{postcode}

### Currency & Finance
- Latest exchange rates (EUR base): GET https://www.frankfurter.app/latest
- Exchange rates with base: GET https://www.frankfurter.app/latest?from={currency}
- Convert currency: GET https://www.frankfurter.app/latest?from={from}&to={to}
- Crypto tickers: GET https://api.coinlore.net/api/tickers/?start=0&limit=20
- Single coin: GET https://api.coinlore.net/api/ticker/?id={id}

### Food & Drink
- Random brewery: GET https://api.openbrewerydb.org/v1/breweries/random
- Search breweries: GET https://api.openbrewerydb.org/v1/breweries?by_city={city}&per_page=10
- All fruits: GET https://www.fruityvice.com/api/fruit/all
- Fruit by name: GET https://www.fruityvice.com/api/fruit/{name}
- Random Brewdog beer: GET https://api.punkapi.com/v2/beers/random
- Beer list: GET https://api.punkapi.com/v2/beers?page=1&per_page=10

### People, Quotes & Personality
- Random advice: GET https://api.adviceslip.com/advice
- Kanye West quote: GET https://api.kanye.rest
- Zen quote: GET https://zenquotes.io/api/random
- Random inspirational quote: GET https://api.quotable.io/random
- Ron Swanson quote: GET https://ron-swanson-quotes.herokuapp.com/v2/quotes
- Stoic quote: GET https://stoic.tekloon.net/stoic-quote

### News
- Spaceflight news articles: GET https://api.spaceflightnewsapi.net/v4/articles/?limit=10
- Spaceflight news blogs: GET https://api.spaceflightnewsapi.net/v4/blogs/?limit=10

### Open Data & Government
- Nobel Prize laureates: GET https://api.nobelprize.org/2.1/laureates?limit=10
- Nobel Prize by year: GET https://api.nobelprize.org/2.1/laureates?nobelPrizeYear={year}
- FBI Most Wanted: GET https://api.fbi.gov/wanted/v1/list
- Interpol Red Notices: GET https://interpol.api.bund.dev/notices/red
- UK Bank Holidays: GET https://www.gov.uk/bank-holidays.json
- US public holidays: GET https://date.nager.at/api/v3/PublicHolidays/{year}/{countryCode}

### Test & Fake Data
- JSONPlaceholder posts: GET https://jsonplaceholder.typicode.com/posts
- JSONPlaceholder single post: GET https://jsonplaceholder.typicode.com/posts/{id}
- JSONPlaceholder users: GET https://jsonplaceholder.typicode.com/users
- Random user profiles: GET https://randomuser.me/api/?results=5
- FakerAPI persons: GET https://fakerapi.it/api/v1/persons?_quantity=5
- FakerAPI companies: GET https://fakerapi.it/api/v1/companies?_quantity=5
- Random yes/no: GET https://yesno.wtf/api
- Robot avatar image: GET https://robohash.org/{any_string}.png
- Lorem Picsum random image info: GET https://picsum.photos/v2/list?limit=10
- Random commit message: GET https://whatthecommit.com/index.txt

### Sports & Fitness
- NBA players: GET https://www.balldontlie.io/api/v1/players?per_page=10
- NBA games: GET https://www.balldontlie.io/api/v1/games?seasons[]=2023
- Available football leagues: GET https://www.openligadb.de/api/getavailableleagues
- Squiggle AFL fixtures: GET https://api.squiggle.com.au/?q=games;year={year}

### Music
- Random music genre: GET https://binaryjazz.us/wp-json/genrenator/v1/genre/
- Radio browser servers: GET https://all.api.radio-browser.info/json/servers
- Radio browser top stations: GET https://de1.api.radio-browser.info/json/stations/topclick/10

### Security / Anti-Malware
- URLhaus recent URLs: POST https://urlhaus-api.abuse.ch/v1/urls/ (body: {limit: 10})

### Development Utilities
- QR code generator (returns image): GET https://api.qrserver.com/v1/create-qr-code/?size=150x150&data={text}
- Cloudflare trace info: GET https://cloudflare.com/cdn-cgi/trace
- HTTP status codes info: GET https://httpstatuses.io/{code}

---

**EXAMPLES:**

Get a random Pokémon:
\`\`\`json
{ "url": "https://pokeapi.co/api/v2/pokemon/pikachu" }
\`\`\`

Get current weather in Paris (lat 48.85, lon 2.35):
\`\`\`json
{ "url": "https://api.open-meteo.com/v1/forecast", "queryParams": { "latitude": "48.85", "longitude": "2.35", "current_weather": "true" } }
\`\`\`

Search for open trivia questions:
\`\`\`json
{ "url": "https://opentdb.com/api.php", "queryParams": { "amount": "5", "category": "18", "type": "multiple" } }
\`\`\`

Get exchange rates:
\`\`\`json
{ "url": "https://www.frankfurter.app/latest", "queryParams": { "from": "USD", "to": "EUR,GBP,JPY" } }
\`\`\`

Get NASA APOD:
\`\`\`json
{ "url": "https://api.nasa.gov/planetary/apod", "queryParams": { "api_key": "DEMO_KEY" } }
\`\`\``,

  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'url',
        type: 'string',
        description: 'Full URL to call, including the base URL and path (e.g. "https://pokeapi.co/api/v2/pokemon/pikachu"). Query params can be included here or passed separately via queryParams.',
        required: true,
      },
      {
        name: 'method',
        type: 'string',
        description: 'HTTP method: GET (default), POST, PUT, PATCH, DELETE',
        required: false,
      },
      {
        name: 'queryParams',
        type: 'object',
        description: 'Query string parameters to append to the URL (e.g. { "limit": "10", "page": "1" })',
        required: false,
      },
      {
        name: 'headers',
        type: 'object',
        description: 'Additional request headers (e.g. { "Accept": "application/json" })',
        required: false,
      },
      {
        name: 'body',
        type: 'object',
        description: 'Request body for POST/PUT/PATCH requests',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { url: 'https://pokeapi.co/api/v2/pokemon/pikachu' },
        expectedOutput: {
          status: 'success',
          message: 'GET https://pokeapi.co/api/v2/pokemon/pikachu — 200 OK',
          format: 'json',
          data: { name: 'pikachu', height: 4, weight: 60 },
        },
        description: 'Fetch Pokémon data',
      },
      {
        input: {
          url: 'https://api.open-meteo.com/v1/forecast',
          queryParams: { latitude: '48.85', longitude: '2.35', current_weather: 'true' },
        },
        expectedOutput: {
          status: 'success',
          message: 'GET https://api.open-meteo.com/v1/forecast — 200 OK',
          format: 'json',
          data: { current_weather: { temperature: 15.2, windspeed: 12.1 } },
        },
        description: 'Get current weather in Paris via Open-Meteo',
      },
      {
        input: {
          url: 'https://icanhazdadjoke.com/',
          headers: { Accept: 'application/json' },
        },
        expectedOutput: {
          status: 'success',
          message: 'GET https://icanhazdadjoke.com/ — 200 OK',
          format: 'json',
          data: { id: 'abc123', joke: 'Why did the scarecrow win an award?...', status: 200 },
        },
        description: 'Fetch a random dad joke (requires Accept: application/json header)',
      },
    ],
    bad: [
      {
        input: { url: 'https://api.stripe.com/v1/customers' },
        error: 'Request failed: 401 Unauthorized',
        why: 'Stripe requires authentication — this tool is for public, no-auth APIs only',
        fix: 'Use api_connect with a connectionId for authenticated APIs',
      },
    ],
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { url, method = 'GET', queryParams, headers, body } = params;

    if (!url) {
      return createErrorResult('url is required', 'Provide the full URL to call.', 'validation');
    }

    try {
      let fullUrl = url;

      if (queryParams && typeof queryParams === 'object' && Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams(
          Object.entries(queryParams).map(([k, v]) => [k, String(v)])
        );
        const separator = url.includes('?') ? '&' : '?';
        fullUrl = `${url}${separator}${searchParams.toString()}`;
      }

      const requestInit: RequestInit = {
        method: method.toUpperCase(),
        headers: {
          Accept: 'application/json',
          ...headers,
        },
      };

      if (body && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
        requestInit.headers = {
          ...requestInit.headers,
          'Content-Type': 'application/json',
        };
        requestInit.body = JSON.stringify(body);
      }

      console.log(`[public_api_query] ${method.toUpperCase()} ${fullUrl}`);

      const response = await fetch(fullUrl, requestInit);

      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      let responseData: any;

      if (isJson) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        return createErrorResult(
          `Request failed: ${response.status} ${response.statusText}`,
          typeof responseData === 'string' ? responseData : JSON.stringify(responseData),
          'execution'
        );
      }

      return createSuccessResult(
        `${method.toUpperCase()} ${fullUrl} — ${response.status} OK`,
        responseData,
        { format: isJson ? 'json' : 'plaintext' }
      );
    } catch (error: any) {
      console.error('[public_api_query] Error:', error);
      return createErrorResult(
        `Request failed: ${error.message}`,
        error,
        'system'
      );
    }
  },
};
